import React, { useState, useEffect, useCallback} from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  ClipboardList, Plus, Search, Filter, RefreshCw, Eye, CheckCircle2,
  Clock, Loader2, Package, MapPin
} from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PickListPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [pickLists, setPickLists] = useState([]);
  const [filteredPickLists, setFilteredPickLists] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  useEffect(() => {
    loadPickLists();
  }, []);


  const loadPickLists = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/warehouse/pick-lists`);
      setPickLists(response.data);
    } catch (error) {
      console.error('Error loading pick lists:', error);
      toast.error('Failed to load pick lists');
    } finally {
      setLoading(false);
    }
  };

  const filterPickLists = useCallback(() => {
    let filtered = [...pickLists];
    if (statusFilter !== 'ALL') {
      filtered = filtered.filter(p => p.status === statusFilter);
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p =>
        p.pick_list_number?.toLowerCase().includes(term) ||
        p.wave_number?.toLowerCase().includes(term)
      );
    }
    setFilteredPickLists(filtered);
  }, [pickLists, searchTerm, statusFilter]);

  useEffect(() => {
    filterPickLists();
  }, [filterPickLists]);

  const getStatusColor = (status) => {
    const colors = {
      'NOT_STARTED': 'bg-gray-100 text-gray-800',
      'IN_PROGRESS': 'bg-yellow-100 text-yellow-800',
      'PARTIALLY_PICKED': 'bg-orange-100 text-orange-800',
      'PICKED': 'bg-green-100 text-green-800',
      'CANCELLED': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'NOT_STARTED': return <Clock className="w-4 h-4" />;
      case 'PICKED': return <CheckCircle2 className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="pick-list-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <ClipboardList className="w-7 h-7 text-amber-600" />
              Pick Lists
            </h1>
            <p className="text-sm text-gray-500">Wave picking for outbound deliveries</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/warehouse/shipments')}>
              Shipments
            </Button>
            <Button onClick={() => navigate('/warehouse/pick-lists/create')} className="bg-amber-600 hover:bg-amber-700">
              <Plus className="w-4 h-4 mr-2" />
              Create Pick List
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px] max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search by pick list, wave..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="ALL">All Status</option>
                <option value="NOT_STARTED">Not Started</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="PICKED">Picked</option>
              </select>
            </div>
            <Button variant="outline" size="sm" onClick={loadPickLists}>
              <RefreshCw className="w-4 h-4 mr-1" />Refresh
            </Button>
          </div>
        </div>

        {/* Results */}
        <div className="bg-white rounded-lg shadow-sm border">
          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="w-12 h-12 animate-spin text-amber-600 mx-auto mb-4" />
              <p className="text-gray-600">Loading pick lists...</p>
            </div>
          ) : filteredPickLists.length === 0 ? (
            <div className="p-8 text-center">
              <ClipboardList className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-600">No pick lists found</p>
              <Button onClick={() => navigate('/warehouse/pick-lists/create')} className="mt-4" variant="outline">
                Create Pick List
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pick List #</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Wave</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shipping Point</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Items</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Completion</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Deliveries</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredPickLists.map((pl) => (
                    <tr key={pl.pick_list_number} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-amber-600">{pl.pick_list_number}</td>
                      <td className="px-4 py-3 text-gray-600">{pl.wave_number || '-'}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(pl.status)}`}>
                          {getStatusIcon(pl.status)}
                          {pl.status?.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{pl.shipping_point}</td>
                      <td className="px-4 py-3 text-center">{pl.total_items}</td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className={`h-full ${pl.pick_completion_percent >= 100 ? 'bg-green-500' : 'bg-amber-500'}`}
                              style={{ width: `${pl.pick_completion_percent}%` }}
                            />
                          </div>
                          <span className="text-sm text-gray-600">{pl.pick_completion_percent}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1 flex-wrap">
                          {pl.delivery_numbers?.slice(0, 3).map((del, i) => (
                            <span key={i} className="px-1.5 py-0.5 bg-gray-100 rounded text-xs">{del}</span>
                          ))}
                          {pl.delivery_numbers?.length > 3 && (
                            <span className="text-xs text-gray-500">+{pl.delivery_numbers.length - 3}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/warehouse/pick-lists/${pl.pick_list_number}`)}
                          className="text-amber-600 hover:text-amber-700"
                        >
                          <Eye className="w-4 h-4 mr-1" />View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-4 text-sm text-gray-500">
          Showing {filteredPickLists.length} of {pickLists.length} pick lists
        </div>
      </div>
    </Layout>
  );
};

export default PickListPage;
